import "date-fns";
import React from "react";
import { v4 as uuidv4 } from "uuid";

import { Form, Formik } from "formik";
import * as Yup from "yup";

import LoadingOverlay from "./LoadingOverlay";
import { AutoCompleteInput } from "./AutoCompleteInput";
import { TimeInput } from "./TimeInput";
import { RadioComponent } from "./RadioComponent";
import { FileInput } from "./FileInput";
import { AlertDialog } from "./Dialogs";

import {
    Select,
    DateInput,
    CheckboxComponent,
    PasswordInput,
    TextInput,
    ImageInput
} from "../widgets";

import { t } from "../services";
import { appContext } from "../AppContext";
import { listMetadata } from "../metadata";
import { CAN_I_SWITCH } from "../events/Gui";

import styles from "./EditComponent.module.styl";

const normalizeSections = (projection) => projection?.sections || [];

const normalizeFields = (section) => section?.fields || [];

const isDisabled = (field, readOnly) => {
    return field.disabled !== undefined ? field.disabled : readOnly;
};

const getLabel = (field) => {
    return field.label ? t(field.label) : t(field.name);
};

const getSpan = (field) => {
    const span = field.span || 12;
    return Math.min(Math.max(span, 1), 12);
};

const fieldStyle = (field) => ({
    gridColumn: `span ${getSpan(field)}`
});

const sortFields = (fields) => {
    return [...(fields || [])].sort((a, b) => {
        const ay = a.y || 0;
        const by = b.y || 0;

        if (ay !== by) {
            return ay - by;
        }

        return (a.x || 0) - (b.x || 0);
    });
};

const resolveList = (field) => {
    if (field.list) {
        return field.list;
    }

    if (field.metadata) {
        return field.metadata();
    }

    if (field.entity || field.owner) {
        return listMetadata[field.entity || field.owner]?.[field.name] || [];
    }

    return [];
};

export const buildEmptyObject = (projection) => {
    const empty = {};

    normalizeSections(projection).forEach((section) => {
        normalizeFields(section).forEach((field) => {
            switch (field.inputType || field.type) {
                case "checkbox":
                    empty[field.name] = false;
                    break;

                case "date":
                case "time":
                case "image":
                case "file":
                    empty[field.name] = null;
                    break;

                default:
                    empty[field.name] = "";
                    break;
            }
        });
    });

    return empty;
};

const getDefaultNumberValidationRule = () => {
    return Yup.string().nullable().test(
        "numeric",
        "Must be numeric",
        function (field) {
            if (field != null && field !== "") {
                return /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(field);
            }

            return true;
        }
    );
};

const buildRules = (projection) => {
    const rules = {};

    normalizeSections(projection).forEach((section) => {
        normalizeFields(section).forEach((field) => {
            if (field.validation) {
                rules[field.name] = field.validation;
                return;
            }

            if ((field.inputType || field.type) === "number") {
                rules[field.name] = getDefaultNumberValidationRule();
            }
        });
    });

    return rules;
};

const validationError = (field, touched, errors) => {
    return errors[field.name] && touched[field.name] ? (
        <div className={styles.validationError}>{t(errors[field.name])}</div>
    ) : null;
};

const FieldShell = ({ field, children }) => {
    return (
        <div className={styles.field} style={fieldStyle(field)}>
            {children}
        </div>
    );
};

const renderInput = (field, index, values, touched, errors, readOnly, gui) => {
    const display = field.display ? field.display(values) : true;

    if (!display) {
        return null;
    }

    const inputType = field.inputType || field.type || "text";
    const label = getLabel(field);
    const disabled = isDisabled(field, readOnly);

    switch (inputType) {
        case "text":
        case "number":
        case "textarea":
            return (
                <FieldShell key={field.name} field={field}>
                    <TextInput
                        label={label}
                        name={field.name}
                        disabled={disabled}
                        type={inputType === "textarea" ? "text" : inputType}
                        multiline={inputType === "textarea"}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "checkbox":
            return (
                <FieldShell key={field.name} field={field}>
                    <CheckboxComponent
                        name={field.name}
                        label={label}
                        language={appContext.getLanguage()}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "radio":
            return (
                <FieldShell key={field.name} field={field}>
                    <RadioComponent
                        name={field.name}
                        options={field.options || []}
                        label={label}
                        defaultValue={field.defaultValue}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "date":
        case "timestampz":
            return (
                <FieldShell key={field.name} field={field}>
                    <DateInput
                        name={field.name}
                        label={label}
                        language={appContext.getLanguage()}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "time":
            return (
                <FieldShell key={field.name} field={field}>
                    <TimeInput
                        name={field.name}
                        label={label}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "select":
        case "list":
            return (
                <FieldShell key={field.name} field={field}>
                    <Select
                        name={field.name}
                        label={label}
                        id={field.name}
                        list={resolveList(field)}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "autocomplete":
            return (
                <FieldShell key={field.name} field={field}>
                    <AutoCompleteInput
                        name={field.name}
                        disabled={disabled}
                        options={field.metadata ? field.metadata() : field.options || []}
                        label={label}
                        handleChange={field.handleChange}
                        observable={field.observable}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "password":
            return (
                <FieldShell key={field.name} field={field}>
                    <PasswordInput
                        label={index === 0 ? "" : label}
                        name={field.name}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "image":
            return (
                <FieldShell key={field.name} field={field}>
                    <ImageInput
                        name={field.name}
                        disabled={disabled}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "file":
            return (
                <FieldShell key={field.name} field={field}>
                    <FileInput
                        uploadUrl={field.uploadUrl(values.id)}
                        loadData={async () => field.loadData(values.id)}
                        previewUrl={(id) => field.previewUrl(id)}
                        handleDelete={(id) => field.handleDelete(id)}
                        handleClick={async (id) => field.handleClick(id)}
                        updateFileData={field.updateFileData}
                        readOnly={disabled}
                        label={field.label}
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        case "custom":
            return (
                <FieldShell key={field.name} field={field}>
                    {field.component(field.name, disabled, values || null, gui)}
                    {validationError(field, touched, errors)}
                </FieldShell>
            );

        default:
            return (
                <FieldShell key={field.name} field={field}>
                    <TextInput
                        label={label}
                        name={field.name}
                        disabled={disabled}
                        type="text"
                    />
                    {validationError(field, touched, errors)}
                </FieldShell>
            );
    }
};

const renderSection = (section, values, touched, errors, readOnly, gui) => {
    const display = section.display ? section.display(values) : true;

    if (!display) {
        return null;
    }

    return (
        <section key={section.id || section.title} className={styles.section}>
            {section.title ? <h2 className={styles.sectionTitle}>{t(section.title)}</h2> : null}
            {section.description ? <p className={styles.sectionDescription}>{t(section.description)}</p> : null}

            <div className={styles.grid}>
                {sortFields(normalizeFields(section)).map((field, index) => {
                    return renderInput(field, index, values, touched, errors, readOnly, gui);
                })}
            </div>
        </section>
    );
};

export class EditComponent extends React.Component {
    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        CAN_I_SWITCH.pickUpThePhone(this.listen);

        this.state = {
            data: undefined,
            loading: true,
            loadingMessage: "",
            closeRequested: undefined
        };

        this.dirty = false;
        this.formState = {};
        this.validationSchema = Yup.object().shape(buildRules(props.projection));
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(previousProps) {
        if (
            previousProps.loadData !== this.props.loadData ||
            previousProps.projection !== this.props.projection
        ) {
            this.validationSchema = Yup.object().shape(buildRules(this.props.projection));
            this.load();
        }
    }

    load = () => {
        this.setState({
            loading: true,
            loadingMessage: ""
        });

        this.props.loadData().then((initialValues) => {
            this.formState.data = initialValues || {};

            this.setState({
                data: initialValues || {},
                loading: false,
                loadingMessage: ""
            });
        });
    };

    refresh = () => {
        this.load();
    };

    listen = (closeMe) => {
        if (!this.isDirty()) {
            closeMe(true);
            return;
        }

        this.setState({ closeRequested: closeMe });
    };

    isDirty = () => {
        return this.dirty;
    };

    handleDirty = (dirty, values) => {
        this.dirty = dirty;
        this.formState.data = values;
    };

    getFormState = () => {
        return this.formState;
    };

    save = () => {
        return new Promise((resolve) => {
            this.formRef.current.submitForm().then((response) => {
                resolve(response);
            });
        });
    };

    getData = () => {
        return this.state.data;
    };

    handleDialogCancel = () => {
        this.state.closeRequested(false);
        this.setState({ closeRequested: undefined });
    };

    handleSave = () => {
        this.save().then(() => {
            this.state.closeRequested(true);
            this.setState({ closeRequested: undefined });
        });
    };

    handleDontSave = () => {
        this.state.closeRequested(true);
        this.setState({ closeRequested: undefined });
    };

    onKeyDown = (event) => {
        if (event.keyCode === 13) {
            event.preventDefault();
        }
    };

    render() {
        const projection = this.props.projection;

        return (
            <>
                <AlertDialog
                    title={t("Save your changes ?")}
                    open={!!(this.state.closeRequested && this.isDirty())}
                    handleClose={this.handleDialogCancel}
                    noAgree={true}
                    save={this.handleSave}
                    dontSave={this.handleDontSave}
                />

                <LoadingOverlay active={this.state.loading} spinner text={this.state.loadingMessage}>
                    {this.state.data && (
                        <div className={styles.shell}>
                            <Formik
                                innerRef={this.formRef}
                                enableReinitialize
                                initialValues={this.state.data}
                                validationSchema={this.validationSchema}
                                onSubmit={(values, actions) => {
                                    this.props.onSubmit(values).then((response) => {
                                        actions.setSubmitting(false);
                                        this.handleDirty(false, values);
                                        return response;
                                    });
                                }}
                            >
                                {({ errors, touched, dirty, values }) => (
                                    <Form className={styles.form} onKeyDown={this.onKeyDown}>
                                        {projection?.title ? (
                                            <header className={styles.header}>
                                                <h1 className={styles.title}>
                                                    {typeof projection.title === "function"
                                                        ? projection.title(values)
                                                        : t(projection.title)}
                                                </h1>
                                            </header>
                                        ) : null}

                                        {this.props.actions ? (
                                            <div className={styles.actions}>
                                                {this.props.actions(values)}
                                            </div>
                                        ) : null}

                                        <div className={styles.sections}>
                                            {normalizeSections(projection).map((section) => {
                                                return renderSection(
                                                    section,
                                                    values,
                                                    touched,
                                                    errors,
                                                    this.props.readOnly,
                                                    this.props.gui
                                                );
                                            })}
                                        </div>

                                        {this.handleDirty(dirty, values)}
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    )}
                </LoadingOverlay>
            </>
        );
    }
}

export const displayEdit = ({
    gui,
    projection,
    loadData,
    onSubmit,
    readOnly = false,
    actions,
    getContext
}) => {
    const uuid = uuidv4();

    return {
        uuid,
        view: () => (
            <EditComponent
                key={uuid}
                gui={gui}
                projection={projection}
                loadData={loadData}
                onSubmit={onSubmit}
                readOnly={readOnly}
                actions={actions}
            />
        ),
        getContext: getContext ? getContext(gui) : undefined
    };
};